import { useEffect, useId } from 'react';

import ListItem from './ListItem';
import { FactoringInfoGraphicFragmentFragment } from '../../__generated__/graphql';
import Section from '../../layout/Section';
import SectionTitle from '../../layout/Section/SectionTitle';
import { ContentBlock } from '../types';

import style from './factoring-info-graphic.module.css';

const drawLinesBetweenLightBulbs = (sectionId: string) => {
  const canvas = document.querySelector<HTMLCanvasElement>(`#${sectionId} canvas`);

  if (!canvas) {
    return;
  }

  const context = canvas.getContext('2d');

  if (!context) {
    return;
  }

  const lightBulbs = canvas.parentElement?.querySelectorAll(`.${style.lightBulb}`);

  context.strokeStyle = '#646464';
  context.lineWidth = 2;

  const canvasPosition = canvas.getBoundingClientRect();

  const canvasY = canvasPosition.y;

  const firstLightBulbRect = lightBulbs?.[0]?.getBoundingClientRect();
  const firstLightBulbX = firstLightBulbRect?.x ?? 0;

  // making sure the slope of the line stays mostly consistent as the canvas resizes
  context.moveTo(Math.min(firstLightBulbX - 800, 0), 0);

  lightBulbs?.forEach(lightBulb => {
    const {
      height,
      left,
      top,
      width,
    } = lightBulb.getBoundingClientRect();

    const halfHeight = height / 2;

    const topPosition = top + halfHeight - canvasY;

    context.lineTo(left + width / 2, topPosition);
  });

  const fallbackEndingY = lightBulbs?.length === 3 ? 0 : canvasPosition.height * 3 / 2;
  const endingY = canvasPosition.width < 1100 ? canvasPosition.height : fallbackEndingY;

  // making sure the slope of the line stays mostly consistent as the canvas resizes
  const endingX = canvasPosition.width < 1100 ? canvasPosition.width : Math.max(2500, canvasPosition.right + 400);

  context.lineTo(endingX, endingY);

  context.stroke();
};

const setCanvasSize = (sectionId: string) => {
  const canvas = document.querySelector<HTMLCanvasElement>(`#${sectionId} canvas`);
  const innerContainer = document.querySelector(`#${sectionId} .${style.sectionInnerContainer}`);
  const list = canvas?.parentElement?.querySelector(`.${style.list}`);

  const width = innerContainer?.clientWidth;
  const height = list?.clientHeight;

  if (canvas) {
    canvas.width = width ?? 0;
    canvas.height = height ?? 0;
  }

  drawLinesBetweenLightBulbs(sectionId);
};

const FactoringInfoGraphic: ContentBlock<FactoringInfoGraphicFragmentFragment> = ({ fragment }) => {
  const sectionId = useId().replaceAll(':', '');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleCanvasResize = () => {
      setCanvasSize(sectionId);
    };

    handleCanvasResize();

    window.addEventListener('resize', handleCanvasResize);

    return () => {
      window.removeEventListener('resize', handleCanvasResize);
    };
  }, [sectionId]);

  const contentBlockProps = fragment.factoringInfoGraphic;

  if (!contentBlockProps) {
    return null;
  }

  const items = contentBlockProps.items;

  return (
    <Section
      backgroundColor="black"
      id={sectionId}
      innerContainerClassName={style.sectionInnerContainer}
    >
      <div className={style.sectionTitleContainer}>
        <SectionTitle>
          {contentBlockProps.title}
        </SectionTitle>
      </div>

      <div className={style.canvasListContainer}>
        <canvas />

        <ol className={style.list}>
          {items?.map(item => {
            if (item?.description) {
              return (
                <ListItem
                  key={item.description}
                  lightBulbClassName={style.lightBulb}
                  text={item.description}
                />
              );
            }
          })}
        </ol>
      </div>
    </Section>
  );
};

export default FactoringInfoGraphic;
